import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'legacy-packages/ft-cb';
import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';

import { defaultValues, INITIAL_STATUS } from './utils';
import './Filters.scss';

/**
 *
 * This method updates the district state used in the Filters
 * @typedef {function(Object)} setActiveDistrict
 */

/**
 * This method updates the section state used in the Filters
 * @typedef {function(Object)} setActiveSection
 */

/**
 * This method updates the status state used in the Filters
 * @typedef {function(Object)} setActiveUserStatus
 */

/**
 * This method updates the student state used in the Filters
 * @typedef {function(Object)} setActiveStudent
 */
/**

/**
 * This method updates the education period state used in the Filters
 * @typedef {function(Object)} setActiveEducationPeriod
 */
/**

 * Filters intended to use under progress dashboard view and course summary view (possibly more)
 * @param {Object} activeDistrict The current selected district in the filter dropdown
 * @param {setActiveDistrict} setActiveDistrict callback to update the selected district
 * @param {Array} districts List of all districts in dropdown
 * @param {Object} activeSection The current selected section in the filter dropdown
 * @param {setActiveDistrict} setActiveSection callback to update the selected section
 * @param {Array} sections List of all sections in dropdown
 * @param {Object} activeUserStatus The current selected status in the filter dropdown
 * @param {setActiveStudent} setActiveUserStatus callback to update the selected status
 * @param {Object} activeStudent The current selected student in the filter dropdown
 * @param {setActiveStudent} setActiveStudent callback to update the selected student
 * @param {Array} students List of all students in dropdown
 * @param {Array} allEducationPeriods List of all education periods in the filter dropdown
 * @param {Object} activeEducationPeriod The current selected education period
 * @param {setActiveEducationPeriod} setActiveEducationPeriod callback to update the selected education period
 * @param {Boolean} showEducationPeriodFilter Feature flag value to determine whether to show the education period filter
 */

const Filters = ({
  activeDistrict,
  setActiveDistrict,
  districts = [],
  activeSection,
  setActiveSection,
  sections = [],
  activeUserStatus,
  setActiveUserStatus,
  activeStudent,
  setActiveStudent,
  students = [],
  hideStudents = false,
  showProgressDashboardUserStatus,
  allEducationPeriods = [],
  activeEducationPeriod,
  setActiveEducationPeriod,
  showEducationPeriodFilter = false,
}) => {
  const showEPFilter =
    allEducationPeriods.length > 1 && showEducationPeriodFilter;

  const showDistrictFilter = districts && districts.length > 1;
  const showUserStatusFilter = showProgressDashboardUserStatus;
  const showStudentFilter = !hideStudents;

  const userStatueOptions = INITIAL_STATUS.values;

  return (
    <div className="HierarchyFilters__filters">
      {showEPFilter && (
        <AutocompleteSelect
          className="HierarchyFilters__filter-dropdown"
          disableDropdown={allEducationPeriods.length <= 1}
          onChange={setActiveEducationPeriod}
          value={activeEducationPeriod}
          placeholder={{
            label: 'Select an Academic Year',
          }}
          options={allEducationPeriods}
          aria-label="Academic year filter"
        />
      )}
      {showDistrictFilter && (
        <AutocompleteSelect
          className="HierarchyFilters__filter-dropdown"
          disableDropdown={districts.length <= 1}
          onChange={setActiveDistrict}
          value={activeDistrict}
          placeholder={{
            label: 'Select a District',
          }}
          options={districts}
          aria-label="District filter"
        />
      )}
      {
        <AutocompleteSelect
          className="HierarchyFilters__filter-dropdown"
          onChange={setActiveSection}
          value={activeSection}
          placeholder={{
            label: 'Select a Class',
          }}
          options={sections}
          aria-label="Section filter"
        />
      }
      {showUserStatusFilter && (
        <AutocompleteSelect
          className="HierarchyFilters__filter-dropdown"
          onChange={setActiveUserStatus}
          value={activeUserStatus}
          placeholder={{
            label: 'Select an option',
          }}
          options={userStatueOptions}
        />
      )}
      {showStudentFilter && (
        <AutocompleteSelect
          className="HierarchyFilters__filter-dropdown"
          onChange={setActiveStudent}
          value={activeStudent}
          placeholder={{
            label: 'Select a Student',
          }}
          options={students}
          icon={
            activeStudent.value !== defaultValues.student.value ? (
              <Icon name="xmark" />
            ) : (
              <Icon className="HierarchyFilters__search-icon" name="search" />
            )
          }
          clearable={activeStudent.value !== defaultValues.student.value}
          customReset={() => setActiveStudent(defaultValues.student)}
          aria-label="Student filter"
        />
      )}
    </div>
  );
};

Filters.propTypes = {
  /** object that describes district info*/
  activeDistrict: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  }).isRequired,
  setActiveDistrict: PropTypes.func.isRequired,
  /** object that describes section info*/
  activeSection: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  }).isRequired,
  setActiveSection: PropTypes.func.isRequired,
  /** object that describes userStatus info*/
  activeUserStatus: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  }).isRequired,
  setActiveUserStatus: PropTypes.func.isRequired,
  /** object that describes student info*/
  activeStudent: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  }),
  setActiveStudent: PropTypes.func,
  districts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ),
  students: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ),
  /** object that describes educationPeriod info*/
  activeEducationPeriod: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
  }).isRequired,
  setActiveEducationPeriod: PropTypes.func.isRequired,
  allEducationPeriods: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ).isRequired,
  showEducationPeriodFilter: PropTypes.bool,
};

Filters.defaultProps = {
  districts: [],
  sections: [],
  students: [],
  activeStudent: defaultValues.student,
  setActiveStudent: () => {},
};

export default Filters;
